import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import {
  getPerformanceReviewMe,
  putPerformanceReviewMe,
  submitPerformanceReviewMe,
} from '../api/performanceReviewApi';
import { PerformanceReviewCycleSetupForm } from './PerformanceReviewCycleSetup';

const inputStyle = {
  width: '100%',
  padding: '0.4rem 0.5rem',
  borderRadius: 6,
  border: '1px solid #e2e8f0',
  fontSize: '0.875rem',
  boxSizing: 'border-box',
  minHeight: 40,
};

const compactCellStyle = {
  padding: '0.22rem 0.35rem',
  fontSize: '0.8rem',
  minHeight: 0,
};

/** One row: # | task | weight | rating | delete — per-task employee comments removed from UI; payload sends empty string. */
const PR_ROW_GRID = '36px minmax(140px, 3.2fr) 72px 104px 40px';

const MAX_PR_TASK_ROWS = 10;

/** Task / objectives: visible height stays between these line counts; scroll inside past max. */
const TASK_OBJECTIVES_MIN_VISIBLE_LINES = 5;
const TASK_OBJECTIVES_MAX_VISIBLE_LINES = 10;

function syncTaskObjectivesTextareaHeight(el) {
  if (!el) return;
  const minLines = TASK_OBJECTIVES_MIN_VISIBLE_LINES;
  const maxLines = TASK_OBJECTIVES_MAX_VISIBLE_LINES;
  const cs = window.getComputedStyle(el);
  const lineHeight = parseFloat(cs.lineHeight);
  const fontSize = parseFloat(cs.fontSize) || 14;
  const lh = Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : fontSize * 1.35;
  const verticalChrome =
    parseFloat(cs.paddingTop) +
    parseFloat(cs.paddingBottom) +
    parseFloat(cs.borderTopWidth) +
    parseFloat(cs.borderBottomWidth);
  const minH = lh * minLines + verticalChrome;
  const maxH = lh * maxLines + verticalChrome;
  el.style.height = 'auto';
  const contentH = el.scrollHeight;
  const next = Math.min(Math.max(contentH, minH), maxH);
  el.style.height = `${next}px`;
  el.style.overflowY = contentH > maxH ? 'auto' : 'hidden';
}

const headerCellNarrow = {
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const labelStyle = { display: 'block', fontSize: '0.68rem', fontWeight: 700, color: '#64748b', marginBottom: 2 };

/** Employee self-review: wording is about your own delivery on each objective (not manager calibration). */
const RATING_HELP_EMPLOYEE =
  '0 = Not Delivered · 1 = Tasks Delayed · 2 = Needs Improvement · 3 = Done on Time · 4 = Fast-Tracked · 5 = Self-Driven';

function emptyLine() {
  return {
    task_description: '',
    weight_percent: 0,
    self_rating: '',
    employee_comment: '',
  };
}

function totalWeight(lines) {
  return lines.reduce((s, l) => s + (Number(l.weight_percent) || 0), 0);
}

export default function PerformanceReviewModal({ open, onClose, year, quarter, allowCycleSetup = false }) {
  const qc = useQueryClient();
  const [achievements, setAchievements] = useState('');
  const [support, setSupport] = useState('');
  const [lines, setLines] = useState([emptyLine()]);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['performance-review-me', year, quarter],
    queryFn: () => getPerformanceReviewMe(year, quarter),
    enabled: open && year != null && quarter != null,
  });

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const submission = data?.submission;
  const cycle = data?.cycle;
  const status = submission?.status || 'draft';
  const canEmployeeEditAndSubmit = status === 'draft' || status === 'revision_requested';
  const readOnlyEmployee = status === 'submitted' || status === 'published';
  const showManagerToEmployee = status === 'published';
  const showExecToEmployee =
    showManagerToEmployee &&
    (submission?.lines || []).some(
      (l) =>
        (l.superadmin_rating != null && l.superadmin_rating !== '') ||
        (l.superadmin_comment && String(l.superadmin_comment).trim()),
    );

  useEffect(() => {
    if (!open || !data) return;
    const sub = data.submission;
    if (!sub) {
      setAchievements('');
      setSupport('');
      setLines([emptyLine()]);
      return;
    }
    setAchievements(sub.employee_achievements_text || '');
    setSupport(sub.employee_support_requests_text || '');
    const subStatus = sub.status;
    const linesLocked = subStatus === 'submitted' || subStatus === 'published';
    let ls = (sub.lines || []).length
      ? sub.lines.map((l) => ({
          id: l.id,
          task_description: l.task_description || '',
          weight_percent: l.weight_percent ?? 0,
          self_rating: l.self_rating != null ? String(l.self_rating) : '',
          employee_comment: l.employee_comment || '',
          manager_rating: l.manager_rating,
          manager_comment: l.manager_comment,
          superadmin_rating: l.superadmin_rating,
          superadmin_comment: l.superadmin_comment,
        }))
      : [emptyLine()];
    if (!linesLocked && ls.length > MAX_PR_TASK_ROWS) {
      ls = ls.slice(0, MAX_PR_TASK_ROWS);
    }
    setLines(ls);
  }, [open, data]);

  useLayoutEffect(() => {
    if (!open) return;
    const run = () => {
      document.querySelectorAll('textarea[data-pr-autosize="task-objectives"]').forEach((el) => {
        syncTaskObjectivesTextareaHeight(el);
      });
    };
    run();
    const id = requestAnimationFrame(run);
    return () => cancelAnimationFrame(id);
  }, [open, lines, readOnlyEmployee]);

  const sumW = useMemo(() => totalWeight(lines), [lines]);
  const canSubmitWeights =
    Math.abs(sumW - 100) <= 0.02 &&
    lines.length > 0 &&
    lines.every((l) => {
      const task = String(l.task_description || '').trim();
      const sr = parseInt(l.self_rating, 10);
      return task && Number.isFinite(sr) && sr >= 0 && sr <= 5;
    });

  const saveMutation = useMutation({
    mutationFn: (body) => putPerformanceReviewMe(body),
    onSuccess: () => {
      toast.success('Draft saved');
      qc.invalidateQueries({ queryKey: ['performance-review-me', year, quarter] });
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Save failed'),
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const body = {
        year,
        quarter,
        employee_achievements_text: achievements,
        employee_support_requests_text: support,
        lines: lines.map((l) => ({
          task_description: l.task_description,
          weight_percent: l.weight_percent,
          self_rating: l.self_rating,
          employee_comment: '',
        })),
      };
      await putPerformanceReviewMe(body);
      return submitPerformanceReviewMe({ year, quarter });
    },
    onSuccess: () => {
      toast.success('Submitted');
      qc.invalidateQueries({ queryKey: ['performance-review-me', year, quarter] });
      qc.invalidateQueries({ queryKey: ['performance-review-status'] });
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Submit failed'),
  });

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const addRow = () =>
    setLines((prev) => (prev.length >= MAX_PR_TASK_ROWS ? prev : [...prev, emptyLine()]));
  const removeRow = (idx) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const handleSaveDraft = () => {
    saveMutation.mutate({
      year,
      quarter,
      employee_achievements_text: achievements,
      employee_support_requests_text: support,
      lines: lines.map((l) => ({
        task_description: l.task_description,
        weight_percent: l.weight_percent,
        self_rating: l.self_rating,
        employee_comment: '',
      })),
    });
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10050,
        padding: '10px',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: 'calc(100vw - 20px)',
          maxWidth: 'calc(100vw - 20px)',
          maxHeight: '92vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        }}
      >
        <div
          style={{
            padding: '0.75rem 1.1rem',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: '#0f172a' }}>Performance review</h2>
            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
              {cycle ? `${cycle.label || `${cycle.year} Q${cycle.quarter}`}` : '—'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'none', fontSize: '1.35rem', cursor: 'pointer', color: '#94a3b8' }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            padding: '0.75rem 1.1rem',
          }}
        >
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', gap: 12 }}>
              <CircularProgress size={36} />
              <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading…</span>
            </div>
          ) : !cycle ? (
            allowCycleSetup ? (
              <div style={{ color: '#334155', fontSize: '0.9rem', lineHeight: 1.55 }}>
                <p style={{ marginTop: 0 }}>
                  There is <strong>no review period</strong> for this quarter yet. Save one below — it calls{' '}
                  <code style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                    POST /api/performance-review/cycles
                  </code>{' '}
                  (same as <code style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>npm run seed:pr-cycle</code>).
                </p>
                <PerformanceReviewCycleSetupForm
                  initialYear={year}
                  initialQuarter={quarter}
                  onSuccess={() => refetch()}
                />
              </div>
            ) : (
              <div style={{ color: '#334155', fontSize: '0.9rem', lineHeight: 1.55 }}>
                <p style={{ marginTop: 0 }}>
                  Performance review is turned on, but <strong>no review period (cycle)</strong> exists in the database yet.
                  An <strong>admin or superadmin</strong> must create the first period from{' '}
                  <strong>Team Activity</strong> or the Performance review flow (when signed in as admin).
                </p>
                <p style={{ marginBottom: 8 }}>
                  Example (with a login token):{' '}
                  <code style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                    POST /api/performance-review/cycles
                  </code>{' '}
                  with body{' '}
                  <code style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                    {`{"year":2026,"quarter":1,"is_open_for_employees":true}`}
                  </code>
                </p>
                <p style={{ color: '#64748b', fontSize: '0.82rem', marginBottom: 0 }}>
                  Or from the repo root run: <code style={{ fontSize: '0.75rem' }}>cd backend && npm run seed:pr-cycle</code>
                </p>
              </div>
            )
          ) : (
            <>
              {status === 'revision_requested' && submission?.manager_revision_feedback && (
                <div
                  style={{
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    color: '#92400e',
                    padding: '0.55rem 0.65rem',
                    borderRadius: 6,
                    fontSize: '0.8rem',
                    marginBottom: '0.65rem',
                    lineHeight: 1.45,
                  }}
                >
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>Changes requested</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{submission.manager_revision_feedback}</div>
                </div>
              )}
              {status === 'submitted' && (
                <div
                  style={{
                    background: '#EAF6FD',
                    color: '#1272B3',
                    padding: '0.5rem 0.65rem',
                    borderRadius: 6,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    marginBottom: '0.65rem',
                  }}
                >
                  Submitted
                </div>
              )}
              {status === 'published' && (
                <div
                  style={{
                    background: '#f0fdf4',
                    color: '#166534',
                    padding: '0.5rem 0.65rem',
                    borderRadius: 6,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    marginBottom: '0.65rem',
                  }}
                >
                  Published — below includes your manager&apos;s feedback (read-only).
                  {showExecToEmployee
                    ? ' Executive calibration is included where shown.'
                    : ''}
                </div>
              )}

              <div style={{ overflowX: 'auto', marginBottom: 2 }}>
              <div style={{ minWidth: 520 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: PR_ROW_GRID,
                  gap: '0.45rem 0.4rem',
                  alignItems: 'center',
                  padding: '0.25rem 0.5rem 0.5rem',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  color: '#64748b',
                  borderBottom: '1px solid #e2e8f0',
                  marginBottom: 6,
                }}
              >
                <span style={{ textAlign: 'center' }}>#</span>
                <span>Task / objectives</span>
                <span style={headerCellNarrow} title="Weight %">
                  Weight %
                </span>
                <span style={headerCellNarrow} title="Rating (0–5)">
                  Rating (0–5)
                </span>
                <span style={{ textAlign: 'center' }} aria-hidden />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {lines.map((line, idx) => (
                  <div
                    key={line.id || `row-${idx}`}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      padding: '0.45rem 0.5rem',
                      background: '#fafafa',
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: PR_ROW_GRID,
                        gap: '0.45rem 0.4rem',
                        alignItems: 'start',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                          fontSize: '0.78rem',
                          fontWeight: 800,
                          color: '#475569',
                          paddingTop: 8,
                        }}
                      >
                        {idx + 1}
                      </div>
                      <textarea
                        data-pr-autosize="task-objectives"
                        title="Task / objectives"
                        rows={1}
                        style={{
                          ...inputStyle,
                          minHeight: 0,
                          resize: 'none',
                          lineHeight: 1.35,
                          overflowX: 'hidden',
                        }}
                        value={line.task_description}
                        disabled={readOnlyEmployee}
                        onChange={(e) => updateLine(idx, { task_description: e.target.value })}
                        onInput={(e) => syncTaskObjectivesTextareaHeight(e.currentTarget)}
                      />
                      <input
                        title="Weight %"
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        style={{
                          ...inputStyle,
                          ...compactCellStyle,
                          textAlign: 'center',
                          width: '100%',
                          maxWidth: '100%',
                        }}
                        disabled={readOnlyEmployee}
                        value={line.weight_percent}
                        onChange={(e) => updateLine(idx, { weight_percent: e.target.value })}
                      />
                      <select
                        title="Self rating 0–5"
                        style={{
                          ...inputStyle,
                          ...compactCellStyle,
                          width: '100%',
                          maxWidth: '100%',
                          background: '#fff',
                          cursor: readOnlyEmployee ? 'default' : 'pointer',
                        }}
                        disabled={readOnlyEmployee}
                        value={line.self_rating}
                        onChange={(e) => updateLine(idx, { self_rating: e.target.value })}
                      >
                        <option value="">—</option>
                        {[0, 1, 2, 3, 4, 5].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 2 }}>
                        {!readOnlyEmployee ? (
                          <IconButton
                            type="button"
                            size="small"
                            aria-label="Remove task"
                            onClick={() => removeRow(idx)}
                            sx={{ color: '#b91c1c' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <span style={{ width: 8 }} />
                        )}
                      </div>
                    </div>
                    {showManagerToEmployee && (
                      <div
                        style={{
                          marginTop: 8,
                          paddingTop: 8,
                          borderTop: '1px dashed #cbd5e1',
                          display: 'grid',
                          gridTemplateColumns: '140px 1fr',
                          gap: '0.65rem',
                          alignItems: 'start',
                        }}
                      >
                        <div>
                          <label style={labelStyle}>Manager rating</label>
                          <input style={{ ...inputStyle, background: '#fff7ed' }} readOnly value={line.manager_rating ?? '—'} />
                        </div>
                        <div>
                          <label style={labelStyle}>Manager comments</label>
                          <textarea
                            rows={2}
                            style={{ ...inputStyle, minHeight: 44, background: '#fff7ed', lineHeight: 1.35, resize: 'vertical' }}
                            readOnly
                            value={line.manager_comment || ''}
                          />
                        </div>
                        {showExecToEmployee &&
                        ((line.superadmin_rating != null && line.superadmin_rating !== '') ||
                          (line.superadmin_comment && String(line.superadmin_comment).trim())) ? (
                          <>
                            <div>
                              <label style={labelStyle}>Executive rating</label>
                              <input
                                style={{ ...inputStyle, background: '#f5f3ff' }}
                                readOnly
                                value={line.superadmin_rating ?? '—'}
                              />
                            </div>
                            <div>
                              <label style={labelStyle}>Executive comments</label>
                              <textarea
                                rows={2}
                                style={{
                                  ...inputStyle,
                                  minHeight: 44,
                                  background: '#f5f3ff',
                                  lineHeight: 1.35,
                                  resize: 'vertical',
                                }}
                                readOnly
                                value={line.superadmin_comment || ''}
                              />
                            </div>
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              </div>
              </div>

              {!readOnlyEmployee && (
                <div style={{ marginTop: '0.45rem' }}>
                  <button
                    type="button"
                    onClick={addRow}
                    disabled={lines.length >= MAX_PR_TASK_ROWS}
                    title={
                      lines.length >= MAX_PR_TASK_ROWS
                        ? `Maximum ${MAX_PR_TASK_ROWS} objectives`
                        : 'Add another row'
                    }
                    style={{
                      padding: '0.35rem 0.75rem',
                      borderRadius: 6,
                      border: '1px dashed #94a3b8',
                      background: '#fff',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      color: lines.length >= MAX_PR_TASK_ROWS ? '#94a3b8' : '#334155',
                      cursor: lines.length >= MAX_PR_TASK_ROWS ? 'not-allowed' : 'pointer',
                      opacity: lines.length >= MAX_PR_TASK_ROWS ? 0.85 : 1,
                    }}
                  >
                    + Add task
                  </button>
                  <span style={{ marginLeft: 10, fontSize: '0.72rem', color: '#64748b' }}>
                    {lines.length}/{MAX_PR_TASK_ROWS} objectives
                  </span>
                </div>
              )}

              <div
                style={{
                  marginTop: '0.65rem',
                  padding: '0.45rem 0.65rem',
                  borderRadius: 6,
                  background: sumW >= 99.98 && sumW <= 100.02 ? '#ecfdf5' : '#fef3c7',
                  color: '#0f172a',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                }}
              >
                Total weight: {sumW.toFixed(1)}% {sumW >= 99.98 && sumW <= 100.02 ? '(ready to submit)' : '(must equal 100% to submit)'}
              </div>

              <div style={{ marginTop: '0.75rem' }}>
                <label style={labelStyle}>Achievements / highlights (optional)</label>
                <textarea
                  rows={3}
                  style={{ ...inputStyle, minHeight: 56, resize: 'vertical', lineHeight: 1.35 }}
                  disabled={readOnlyEmployee}
                  value={achievements}
                  onChange={(e) => setAchievements(e.target.value)}
                />
              </div>
              <div style={{ marginTop: '0.55rem' }}>
                <label style={labelStyle}>Support / resources needed (optional)</label>
                <textarea
                  rows={3}
                  style={{ ...inputStyle, minHeight: 56, resize: 'vertical', lineHeight: 1.35 }}
                  disabled={readOnlyEmployee}
                  value={support}
                  onChange={(e) => setSupport(e.target.value)}
                />
              </div>

              {showManagerToEmployee && (
                <div style={{ marginTop: '0.65rem' }}>
                  <label style={labelStyle}>Manager overall comment</label>
                  <textarea
                    rows={3}
                    style={{ ...inputStyle, minHeight: 56, background: '#fff7ed', lineHeight: 1.35 }}
                    readOnly
                    value={submission?.manager_overall_comment || ''}
                  />
                </div>
              )}
              {showExecToEmployee && submission?.superadmin_overall_comment ? (
                <div style={{ marginTop: '0.55rem' }}>
                  <label style={labelStyle}>Executive overall comment</label>
                  <textarea
                    rows={3}
                    style={{ ...inputStyle, minHeight: 56, background: '#f5f3ff', lineHeight: 1.35 }}
                    readOnly
                    value={submission.superadmin_overall_comment || ''}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>

        <div
          style={{
            flexShrink: 0,
            padding: '0.55rem 1.1rem',
            borderTop: '1px solid #e2e8f0',
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {!isLoading && cycle && (
            <div
              style={{
                padding: '0.4rem 0.55rem',
                borderRadius: 6,
                background: '#f8fafc',
                fontSize: '0.72rem',
                color: '#64748b',
                lineHeight: 1.45,
              }}
            >
              <span style={{ fontWeight: 700, color: '#475569' }}>Self-rating guide: </span>
              {RATING_HELP_EMPLOYEE}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                background: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
            {cycle && canEmployeeEditAndSubmit && (
              <>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={saveMutation.isPending || isFetching}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: 8,
                    border: 'none',
                    background: '#334155',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {saveMutation.isPending ? 'Saving…' : 'Save draft'}
                </button>
                <button
                  type="button"
                  onClick={() => submitMutation.mutate()}
                  disabled={submitMutation.isPending || !canSubmitWeights || isFetching}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: 8,
                    border: 'none',
                    background: canSubmitWeights ? '#2C63AE' : '#94a3b8',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: canSubmitWeights ? 'pointer' : 'not-allowed',
                  }}
                >
                  {submitMutation.isPending ? 'Submitting…' : 'Submit'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
